import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { translations } from '../lib/translations';
import { User, House, Check, Plus, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import SignaturePad from '../components/SignaturePad';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SetupProfilePage() {
  const { updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  const selectedLanguage = localStorage.getItem('selectedLanguage') || 'ITA';
  const t = translations[selectedLanguage];

  const [formData, setFormData] = useState({
    full_name: '',
    birth_place: '',
    birth_date: '',
    residence: '',
    tax_code: '',
    property_city: '',
    property_province: '',
    property_address: '',
    property_number: '',
    cadastral_sheet: '',
    cadastral_parcel: '',
    cadastral_sub: '',
    cin_code: '',
    max_occupants: 4,
    pets_allowed: false,
    preferred_language: selectedLanguage
  });

  const [showAddMoreDialog, setShowAddMoreDialog] = useState(false);
  const [saveSignature, setSaveSignature] = useState(false);
  const sigPadRef = useRef(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const clearSig = () => sigPadRef.current?.clear();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/owner/profile`, formData, { withCredentials: true });
      // Save signature if user opted in and drew something
      if (saveSignature && sigPadRef.current && !sigPadRef.current.isEmpty()) {
        try {
          const signature = sigPadRef.current.toDataURL();
          await axios.post(`${API}/owner/signature`, { signature }, { withCredentials: true });
        } catch (e) {
          // Non-blocking
        }
      }
      updateUser({ preferred_language: selectedLanguage, owner_profile_complete: true });
      toast.success(t.profileSaved);
      // Show dialog to ask if user wants to add more properties
      setShowAddMoreDialog(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvare il profilo');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMore = () => {
    setShowAddMoreDialog(false);
    navigate('/properties');
  };

  const handleGoToDashboard = () => {
    setShowAddMoreDialog(false);
    navigate('/dashboard');
  };

  const nextStep = () => setStep(2);
  const prevStep = () => setStep(1);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-zinc-200 bg-zinc-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-4 sm:py-6">
          <h1 className="text-xl sm:text-2xl font-black text-zinc-900 tracking-tight">{t.ownerProfile}</h1>
          <p className="text-zinc-500 mt-1">{t.completeProfile}</p>
        </div>
      </div>

      {/* Progress */}
      <div className="max-w-3xl mx-auto px-8 py-6">
        <div className="flex items-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-zinc-900' : 'text-zinc-400'}`}>
            <div className={`w-8 h-8 flex items-center justify-center border ${step >= 1 ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-300'}`}>
              {step > 1 ? <Check size={16} weight="bold" /> : '1'}
            </div>
            <span className="font-medium text-sm">{t.personalData}</span>
          </div>
          <div className="flex-1 h-px bg-zinc-200" />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-zinc-900' : 'text-zinc-400'}`}>
            <div className={`w-8 h-8 flex items-center justify-center border ${step >= 2 ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-300'}`}>
              2
            </div>
            <span className="font-medium text-sm">{t.propertyData}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {step === 1 && (
            <div className="animate-fade-in space-y-6">
              <div className="p-6 border border-zinc-200 bg-white">
                <div className="flex items-center gap-3 mb-6">
                  <User size={24} weight="bold" className="text-zinc-700" />
                  <h2 className="text-lg font-bold text-zinc-900">{t.personalData}</h2>
                </div>

                <div className="grid gap-5">
                  <div>
                    <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.fullName}</Label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => handleChange('full_name', e.target.value)}
                      required
                      className="rounded-none mt-2"
                      data-testid="profile-fullname-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.birthPlace}</Label>
                      <Input
                        value={formData.birth_place}
                        onChange={(e) => handleChange('birth_place', e.target.value)}
                        required
                        className="rounded-none mt-2"
                        data-testid="profile-birthplace-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.birthDate}</Label>
                      <Input
                        type="date"
                        value={formData.birth_date}
                        onChange={(e) => handleChange('birth_date', e.target.value)}
                        required
                        className="rounded-none mt-2"
                        data-testid="profile-birthdate-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.residence}</Label>
                    <Input
                      value={formData.residence}
                      onChange={(e) => handleChange('residence', e.target.value)}
                      required
                      className="rounded-none mt-2"
                      data-testid="profile-residence-input"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.taxCode}</Label>
                    <Input
                      value={formData.tax_code}
                      onChange={(e) => handleChange('tax_code', e.target.value.toUpperCase())}
                      required
                      className="rounded-none mt-2"
                      data-testid="profile-taxcode-input"
                    />
                  </div>
                </div>
              </div>

              <Button
                type="button"
                onClick={nextStep}
                className="w-full rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider py-6"
                data-testid="profile-next-button"
              >
                {t.continue}
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="animate-fade-in space-y-6">
              <div className="p-6 border border-zinc-200 bg-white">
                <div className="flex items-center gap-3 mb-6">
                  <House size={24} weight="bold" className="text-zinc-700" />
                  <h2 className="text-lg font-bold text-zinc-900">{t.propertyData}</h2>
                </div>

                <div className="grid gap-5">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div className="col-span-2">
                      <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.propertyCity}</Label>
                      <Input
                        value={formData.property_city}
                        onChange={(e) => handleChange('property_city', e.target.value)}
                        required
                        className="rounded-none mt-2"
                        data-testid="profile-city-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.propertyProvince}</Label>
                      <Input
                        value={formData.property_province}
                        onChange={(e) => handleChange('property_province', e.target.value.toUpperCase())}
                        maxLength={2}
                        required
                        className="rounded-none mt-2"
                        data-testid="profile-province-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div className="sm:col-span-3">
                      <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.propertyAddress}</Label>
                      <Input
                        value={formData.property_address}
                        onChange={(e) => handleChange('property_address', e.target.value)}
                        required
                        className="rounded-none mt-2"
                        data-testid="profile-address-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.propertyNumber}</Label>
                      <Input
                        value={formData.property_number}
                        onChange={(e) => handleChange('property_number', e.target.value)}
                        required
                        className="rounded-none mt-2"
                        data-testid="profile-number-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.cadastralSheet}</Label>
                      <Input
                        value={formData.cadastral_sheet}
                        onChange={(e) => handleChange('cadastral_sheet', e.target.value)}
                        required
                        className="rounded-none mt-2"
                        data-testid="profile-sheet-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.cadastralParcel}</Label>
                      <Input
                        value={formData.cadastral_parcel}
                        onChange={(e) => handleChange('cadastral_parcel', e.target.value)}
                        required
                        className="rounded-none mt-2"
                        data-testid="profile-parcel-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.cadastralSub}</Label>
                      <Input
                        value={formData.cadastral_sub}
                        onChange={(e) => handleChange('cadastral_sub', e.target.value)}
                        required
                        className="rounded-none mt-2"
                        data-testid="profile-sub-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.cinCode}</Label>
                    <Input
                      value={formData.cin_code}
                      onChange={(e) => handleChange('cin_code', e.target.value.toUpperCase())}
                      required
                      className="rounded-none mt-2"
                      data-testid="profile-cin-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">{t.maxOccupants}</Label>
                      <Input
                        type="number"
                        min={1}
                        max={20}
                        value={formData.max_occupants}
                        onChange={(e) => handleChange('max_occupants', parseInt(e.target.value))}
                        required
                        className="rounded-none mt-2"
                        data-testid="profile-occupants-input"
                      />
                    </div>
                    <div className="flex items-end">
                      <div className="flex items-center gap-3 p-3 border border-zinc-200 w-full">
                        <Switch
                          checked={formData.pets_allowed}
                          onCheckedChange={(checked) => handleChange('pets_allowed', checked)}
                          data-testid="profile-pets-switch"
                        />
                        <Label className="text-sm font-medium text-zinc-700">{t.petsAllowed}</Label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Signature (optional) */}
              <div className="bg-white border border-zinc-200 p-6 sm:p-8" data-testid="save-signature-section">
                <div className="flex items-start sm:items-center justify-between gap-3 mb-4 flex-col sm:flex-row">
                  <div className="flex items-start gap-3">
                    <PencilSimple size={22} weight="bold" className="text-zinc-700 flex-shrink-0 mt-1 sm:mt-0" />
                    <div>
                      <h2 className="text-lg font-bold text-zinc-900 tracking-tight">Salva la firma <span className="text-xs font-normal text-zinc-400">(opzionale)</span></h2>
                      <p className="text-xs text-zinc-500 mt-0.5">Disegna una volta. Usabile poi su ogni contratto con un click.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Switch
                      checked={saveSignature}
                      onCheckedChange={setSaveSignature}
                      data-testid="save-signature-toggle"
                    />
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-700">Salva firma</Label>
                  </div>
                </div>

                {saveSignature && (
                  <div className="space-y-3 pt-2 border-t border-zinc-100">
                    <div className="border border-zinc-300 bg-zinc-50">
                      <SignaturePad
                        ref={sigPadRef}
                        height={180}
                        data-testid="profile-signature-pad"
                      />
                    </div>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={clearSig}
                        className="rounded-none border-zinc-300 text-xs font-bold uppercase tracking-wider h-8"
                        data-testid="clear-profile-signature"
                      >
                        Pulisci
                      </Button>
                      <p className="text-[10px] text-zinc-500 leading-tight">
                        ℹ️ Verrà salvata in modo cifrato. Sostituibile in qualsiasi momento.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-4">
                <Button
                  type="button"
                  onClick={prevStep}
                  variant="outline"
                  className="flex-1 rounded-none border-zinc-900 text-zinc-900 font-bold uppercase tracking-wider py-6"
                  data-testid="profile-back-button"
                >
                  {t.cancel}
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider py-6"
                  data-testid="profile-save-button"
                >
                  {loading ? '...' : t.save}
                </Button>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Add More Properties Dialog */}
      <Dialog open={showAddMoreDialog} onOpenChange={setShowAddMoreDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-center">
              Hai altre strutture?
            </DialogTitle>
          </DialogHeader>
          <div className="py-6 text-center">
            <House size={48} className="text-zinc-400 mx-auto mb-4" />
            <p className="text-zinc-600 mb-6">
              Vuoi registrare un altro alloggio? Potrai sempre farlo dopo dalla sezione "Miei Alloggi".
            </p>
            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={handleGoToDashboard}
                className="flex-1 rounded-none border-zinc-300"
                data-testid="go-to-dashboard-button"
              >
                No, vai alla Dashboard
              </Button>
              <Button
                onClick={handleAddMore}
                className="flex-1 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold"
                data-testid="add-another-property-button"
              >
                <Plus size={18} className="mr-2" />
                Aggiungi
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
